<script lang="ts" setup>
import { computed, h, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@tanstack/vue-query";
import {
    type DataTableColumns,
    NDataTable,
    NDropdown,
    NPopover,
    NSkeleton,
    NSpin,
    useMessage,
} from "naive-ui";
import {
    bulkSetBankAccountProperScope,
    patchBankAccount,
    searchBankAccountsV2,
} from "../../services/contador/bank-accounts.service";
import { searchPropertiesV2 } from "../../services/contador/properties.service";
import type {
    BankAccountsSearchRequest,
    BankAccountsWhere,
    PropertiesSearchRequest,
    WhereAndOr,
} from "../../models/search";
import type { BankAccount, Comment } from "../../models/contador";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints";
import { queryRef } from "../../router/helpers.ts";
import NotesDrawer from "../../components/NotesDrawer.vue";
import { renderNotesCell } from "../../utils/notesColumn";
import ProperText from "../../components/proper-inputs/proper-text.vue";
import ProperSwitch from "../../components/proper-inputs/proper-switch.vue";
import ProperSelect from "../../components/proper-inputs/proper-select.vue";
import ConfirmationModal from "../../components/ConfirmationModal.vue";
import { useOrganization } from "../../hooks/useOrganization";
import { debounce } from "lodash";

const props = defineProps<{
    showBulkScope?: boolean;
}>();

const { isMobile } = usePortalBreakpoints();
const { organization } = useOrganization();
const message = useMessage();
const route = useRoute();
const router = useRouter();

// Bulk scope state
const selectedRowKeys = ref<string[]>([]);
const isBulkUpdating = ref(false);

async function handleBulkScope(enabled: boolean) {
    if (!organization.value?.id || selectedRowKeys.value.length === 0) return;
    isBulkUpdating.value = true;
    try {
        await bulkSetBankAccountProperScope(organization.value.id, enabled, selectedRowKeys.value);
        await refetchPages();
        const count = selectedRowKeys.value.length;
        selectedRowKeys.value = [];
        message.success(`${count} ${count === 1 ? "account" : "accounts"} set ${enabled ? "In Scope" : "Out of Scope"}`);
    } catch {
        message.error("Failed to update bank accounts");
    } finally {
        isBulkUpdating.value = false;
    }
}

// Drawer states
const showDrawer = ref(false);
const openedBankAccount = ref<BankAccount | null>(null);
const openedBankAccountId = queryRef("bank-account-notes-and-activity", "");
const openedDrawerTab = queryRef("bank-account-drawer-tab", "notes");

// Search state
function stripAccents(input: string): string {
    if (!input) return "";
    try {
        return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch {
        return input;
    }
}
const searchParamKey = "bank-accounts-search";
const properScopeParamKey = "bank-accounts-proper-scope";
const corporateFlagParamKey = "bank-accounts-corporate-flag";
const bankNamesParamKey = "bank-accounts-bank-names";
const propertiesParamKey = "bank-accounts-properties";

function normalizeStringParam(value: unknown): string {
    if (Array.isArray(value)) {
        return value[0] ?? "";
    }
    return typeof value === "string" ? value : "";
}

function normalizeArrayParam(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string" && item.length > 0);
    }
    return typeof value === "string" && value.length > 0 ? [value] : [];
}

function arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
}

function setQueryParam(paramName: string, value: string | string[] | null) {
    const nextQuery = { ...route.query };
    const normalizedValue = Array.isArray(value) ? value : (value ?? "");
    if (Array.isArray(normalizedValue) && normalizedValue.length === 0) {
        delete nextQuery[paramName];
    } else if (!Array.isArray(normalizedValue) && normalizedValue === "") {
        delete nextQuery[paramName];
    } else {
        nextQuery[paramName] = normalizedValue as any;
    }
    router.replace({ query: nextQuery });
}

const searchTerm = ref<string>(normalizeStringParam(route.query[searchParamKey]));
const debouncedSearch = ref<string>("");
const applySearch = debounce((val: string) => {
    debouncedSearch.value = stripAccents((val || "").trim());
}, 300);
watch(
    () => searchTerm.value,
    (val) => {
        applySearch(val);
    },
    { immediate: true },
);

// Filters state
const filterProperScope = ref<string | null>(
    normalizeStringParam(route.query[properScopeParamKey]) || null,
);
const filterCorporateFlag = ref<string | null>(
    normalizeStringParam(route.query[corporateFlagParamKey]) || null,
);
const filterBankNames = ref<string[]>(normalizeArrayParam(route.query[bankNamesParamKey]));
const filterPropertyIds = ref<string[]>(normalizeArrayParam(route.query[propertiesParamKey]));

watch(
    () => route.query[searchParamKey],
    (value) => {
        const next = normalizeStringParam(value);
        if (next !== searchTerm.value) {
            searchTerm.value = next;
        }
    },
);

watch(
    () => route.query[properScopeParamKey],
    (value) => {
        const next = normalizeStringParam(value) || null;
        if (next !== filterProperScope.value) {
            filterProperScope.value = next;
        }
    },
);

watch(
    () => route.query[corporateFlagParamKey],
    (value) => {
        const next = normalizeStringParam(value) || null;
        if (next !== filterCorporateFlag.value) {
            filterCorporateFlag.value = next;
        }
    },
);

watch(
    () => route.query[bankNamesParamKey],
    (value) => {
        const next = normalizeArrayParam(value);
        if (!arraysEqual(next, filterBankNames.value)) {
            filterBankNames.value = next;
        }
    },
);
watch(
    () => route.query[propertiesParamKey],
    (value) => {
        const next = normalizeArrayParam(value);
        if (!arraysEqual(next, filterPropertyIds.value)) {
            filterPropertyIds.value = next;
        }
    },
);

watch(
    () => searchTerm.value,
    (value) => {
        const current = normalizeStringParam(route.query[searchParamKey]);
        if (value !== current) {
            setQueryParam(searchParamKey, value);
        }
    },
);

watch(
    () => filterProperScope.value,
    (value) => {
        const current = normalizeStringParam(route.query[properScopeParamKey]) || null;
        if (value !== current) {
            setQueryParam(properScopeParamKey, value);
        }
    },
);

watch(
    () => filterCorporateFlag.value,
    (value) => {
        const current = normalizeStringParam(route.query[corporateFlagParamKey]) || null;
        if (value !== current) {
            setQueryParam(corporateFlagParamKey, value);
        }
    },
);

watch(
    () => filterBankNames.value,
    (value) => {
        const current = normalizeArrayParam(route.query[bankNamesParamKey]);
        if (!arraysEqual(value, current)) {
            setQueryParam(bankNamesParamKey, value);
        }
    },
    { deep: true },
);
watch(
    () => filterPropertyIds.value,
    (value) => {
        const current = normalizeArrayParam(route.query[propertiesParamKey]);
        if (!arraysEqual(value, current)) {
            setQueryParam(propertiesParamKey, value);
        }
    },
    { deep: true },
);

// Bank name options cache and loading
const filtersPopoverOpen = ref(false);
const bankNameOptions = ref<{ label: string; value: string }[]>([]);
const bankNamesLoading = ref(false);
const bankNamesFirstLoaded = ref(false);
let bankNamesLoadToken = 0;

const propertyOptions = ref<{ label: string; value: string }[]>([]);
const propertiesLoading = ref(false);
const propertiesFirstLoaded = ref(false);
let propertiesLoadToken = 0;

// Property names cache and loader for visible rows
const propertyNameMap = ref<Record<string, string>>({});
const inFlightPropertyIds = ref<Set<string>>(new Set());

function extractPropertyIdsFromRows(rows: BankAccount[]): string[] {
    const ids = new Set<string>();
    for (const row of rows || []) {
        const list = row.propertyBankAccounts || [];
        for (const pba of list) {
            const id = pba?.propertyId;
            if (id) ids.add(id);
        }
    }
    return Array.from(ids);
}

async function ensurePropertyNamesForRows(rows: BankAccount[]): Promise<void> {
    const ids = extractPropertyIdsFromRows(rows || []);
    const missing = ids.filter(
        (id) => !(id in propertyNameMap.value) && !inFlightPropertyIds.value.has(id),
    );
    if (missing.length === 0) return;

    for (const id of missing) inFlightPropertyIds.value.add(id);
    try {
        const payload: PropertiesSearchRequest = {
            where: { id: { in: missing } },
            limit: missing.length,
            offset: 0,
        } as any;
        const resp = await searchPropertiesV2(payload);
        const items = resp?.items ?? [];
        const mapUpdates: Record<string, string> = {};
        for (const p of items) {
            const id = (p as any)?.id;
            if (!id) continue;

            mapUpdates[id] = (p as any)?.name || (p as any)?.shortName || id;
        }
        for (const id of missing) {
            if (!(id in mapUpdates)) {
                mapUpdates[id] = id;
            }
        }
        propertyNameMap.value = { ...propertyNameMap.value, ...mapUpdates };
    } finally {
        for (const id of missing) inFlightPropertyIds.value.delete(id);
    }
}

// Fetch all bank accounts via pagination for filter population
async function fetchAllBankAccountsForFilters() {
    const all: BankAccount[] = [];
    let offset = 0;
    const limit = 1000; // large page to minimize requests

    while (true) {
        const payload: BankAccountsSearchRequest = {
            where: {},
            limit,
            offset,
            include: ["PropertyBankAccounts"],
        } as any;
        const resp = await searchBankAccountsV2(payload);
        const items = resp?.items ?? [];
        if (items.length > 0) {
            all.push(...items);
        }

        const total = resp?.total;
        const count = items.length;
        // Stop when we've fetched all using total if available, otherwise when returned less than limit
        if ((typeof total === "number" && all.length >= total) || count < limit) {
            break;
        }
        offset += count || 0;
    }

    return all;
}

async function refreshBankNames(silent = false) {
    const token = ++bankNamesLoadToken;
    if (!silent) bankNamesLoading.value = !bankNamesFirstLoaded.value;
    try {
        // Fetch all bank accounts to build the distinct list of bank names
        const items = await fetchAllBankAccountsForFilters();
        if (token !== bankNamesLoadToken) return; // ignore stale
        const seen = new Map<string, string>();
        for (const it of items) {
            const raw = (it.bank_name ?? "").trim();
            if (!raw) continue;
            const key = raw.toLowerCase();
            if (!seen.has(key)) {
                seen.set(key, raw);
            }
        }
        const nextOptions = Array.from(seen.values())
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
            .map((v) => ({ label: v, value: v }));
        // ensure currently selected values remain in options even if not present in fetched
        const selected = filterBankNames.value || [];
        for (const sel of selected) {
            if (!sel) continue;
            if (!nextOptions.some((o) => o.value === sel)) {
                nextOptions.push({ label: sel, value: sel });
            }
        }
        // sort again after merging selected extras
        nextOptions.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
        bankNameOptions.value = nextOptions;
        bankNamesFirstLoaded.value = true;
    } catch (e) {
        // swallow
    } finally {
        if (!silent) bankNamesLoading.value = false;
    }
}

async function refreshProperties(silent = false) {
    const token = ++propertiesLoadToken;
    if (!silent) propertiesLoading.value = !propertiesFirstLoaded.value;
    try {
        // Fetch all bank accounts to collect property IDs
        const items = await fetchAllBankAccountsForFilters();
        if (token !== propertiesLoadToken) return;
        const propertyIdSet = new Set<string>();
        for (const it of items) {
            const list = (it as any).propertyBankAccounts || [];
            for (const rel of list) {
                if (rel?.propertyId) propertyIdSet.add(rel.propertyId);
            }
        }
        const ids = Array.from(propertyIdSet);
        if (ids.length === 0) {
            propertyOptions.value = [];
            propertiesFirstLoaded.value = true;
            return;
        }
        // Now resolve property names via properties search
        const propPayload: PropertiesSearchRequest = {
            where: {
                id: { in: ids },
            },
            limit: ids.length,
            offset: 0,
        } as any;
        const propsResp = await searchPropertiesV2(propPayload);
        if (token !== propertiesLoadToken) return;
        const props = propsResp?.items ?? [];
        const options = props
            .map((p) => ({ label: p.name || p.shortName || p.id, value: p.id }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        // keep selected values present
        const selected = filterPropertyIds.value || [];
        for (const sel of selected) {
            if (!sel) continue;
            if (!options.some((o) => o.value === sel)) {
                options.push({ label: sel, value: sel });
            }
        }
        options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        propertyOptions.value = options;
        propertiesFirstLoaded.value = true;
    } catch (e) {
        // ignore
    } finally {
        if (!silent) propertiesLoading.value = false;
    }
}

watch(
    () => filtersPopoverOpen.value,
    (isOpen) => {
        if (isOpen) {
            if (bankNamesFirstLoaded.value) {
                void refreshBankNames(true);
            } else {
                void refreshBankNames(false);
            }
            if (propertiesFirstLoaded.value) {
                void refreshProperties(true);
            } else {
                void refreshProperties(false);
            }
        }
    },
);

const properScopeOptions = [
    { label: "In Proper Scope", value: "true" },
    { label: "Out of Scope", value: "false" },
];
const corporateFlagOptions = [
    { label: "Corporate", value: "true" },
    { label: "Non-corporate", value: "false" },
];

const properScopeFilterBool = computed<boolean | null>(() =>
    filterProperScope.value === "true" ? true : filterProperScope.value === "false" ? false : null,
);
const corporateFlagFilterBool = computed<boolean | null>(() =>
    filterCorporateFlag.value === "true"
        ? true
        : filterCorporateFlag.value === "false"
          ? false
          : null,
);

const activeFiltersCount = computed(() => {
    let count = 0;
    if (filterProperScope.value !== null) count += 1;
    if (filterCorporateFlag.value !== null) count += 1;
    if (filterBankNames.value.length > 0) count += 1;
    if (filterPropertyIds.value.length > 0) count += 1;
    return count;
});
const hasActiveFilters = computed(() => activeFiltersCount.value > 0);

function clearFilters() {
    filterProperScope.value = null;
    filterCorporateFlag.value = null;
    filterBankNames.value = [];
    filterPropertyIds.value = [];
}

const pageLimit = 50;
const bankAccounts = ref<BankAccount[]>([]);
const total = ref<number | undefined>();
const nextOffset = ref(0);
const lastPageCount = ref(0);
const isFetchingMore = ref(false);

const buildV2SearchPayload = (opts: {
    offset: number;
    limit: number;
    includeComments?: boolean;
}) => {
    const where: WhereAndOr<BankAccountsWhere> = { AND: [] };

    if (debouncedSearch.value) {
        where.AND!.push({
            OR: [
                { account_number: { contains: debouncedSearch.value } },
                { account_name: { contains: debouncedSearch.value } },
                { pms: { contains: debouncedSearch.value } },
                { bank_name: { contains: debouncedSearch.value } },
            ],
        });
    }

    if (properScopeFilterBool.value !== null) {
        where.AND!.push({ proper_scope: { equals: !!properScopeFilterBool.value } });
    }
    if (corporateFlagFilterBool.value !== null) {
        where.AND!.push({ is_corporate: { equals: !!corporateFlagFilterBool.value } });
    }

    if (filterBankNames.value.length > 0) {
        where.AND!.push(
            filterBankNames.value.length === 1
                ? { bank_name: { equals: filterBankNames.value[0] } }
                : { bank_name: { in: filterBankNames.value } },
        );
    }

    if (filterPropertyIds.value.length > 0) {
        const ids = [...filterPropertyIds.value];
        (where.AND as any)!.push({
            property_id: {
                in: ids,
            },
        });
    }

    if (opts.includeComments) {
        where.AND!.push({ include_comments: { equals: true } });
    }

    const payload: BankAccountsSearchRequest = {
        where,
        limit: opts.limit,
        offset: opts.offset,
        include: ["PropertyBankAccounts"],
    } as any;

    return payload;
};

const { data: bankAccountsResponse, isLoading: loadingBankAccounts } = useQuery({
    queryKey: computed(() => [
        "bankAccounts-v2",
        pageLimit,
        debouncedSearch.value,
        properScopeFilterBool.value,
        corporateFlagFilterBool.value,
        JSON.stringify(filterBankNames.value),
        JSON.stringify(filterPropertyIds.value),
        // Removed propertyOptions from the query key to avoid table refresh when opening filters
    ]),
    queryFn: async () => {
        return await searchBankAccountsV2(
            buildV2SearchPayload({ offset: 0, limit: pageLimit, includeComments: true }),
        );
    },
});

async function openDrawerForBankAccountId(id: string) {
    let ba = bankAccounts.value.find((b) => b.id === id) || null;

    if (!ba) {
        try {
            const payload: BankAccountsSearchRequest = {
                where: {
                    AND: [{ id: { equals: id } }, { include_comments: { equals: true } }],
                } as WhereAndOr<BankAccountsWhere>,
                limit: 1,
                offset: 0,
            };
            const resp = await searchBankAccountsV2(payload);
            const items = resp?.items ?? [];
            if (items && items.length > 0) {
                ba = items[0] ?? null;
                if (ba && !bankAccounts.value.some((b) => b.id === ba!.id)) {
                    bankAccounts.value = [ba, ...bankAccounts.value];
                }
            }
        } catch (e) {
            // ignore
        }
    }

    if (!ba) {
        message.error("Bank account not found in current list");
        openedBankAccountId.value = "";
        return;
    }

    openedBankAccount.value = ba;
    showDrawer.value = true;
}

watch(
    () => openedBankAccountId.value,
    (id) => {
        if (!id) return;
        void openDrawerForBankAccountId(id);
    },
    { immediate: true },
);

watch(
    () => showDrawer.value,
    (isOpen) => {
        if (!isOpen) {
            openedBankAccountId.value = "";
        }
    },
);

watch(
    bankAccountsResponse,
    (response) => {
        if (!response) {
            bankAccounts.value = [];
            total.value = 0;
            nextOffset.value = 0;
            lastPageCount.value = 0;
            return;
        }

        const items = response.items ?? [];
        bankAccounts.value = items;
        total.value = response.total ?? items.length;
        nextOffset.value = ((response.offset ?? 0) as number) + items.length;
        lastPageCount.value = items.length;
    },
    { immediate: true },
);

watch(
    () => bankAccounts.value,
    (rows) => {
        void ensurePropertyNamesForRows(rows || []);
    },
    { immediate: true, deep: true },
);

const canLoadMore = computed(() => {
    if (total.value !== undefined && total.value !== null) {
        return bankAccounts.value.length < total.value;
    }

    return lastPageCount.value === pageLimit;
});

const loadMoreBankAccounts = async () => {
    if (isFetchingMore.value || loadingBankAccounts.value || !canLoadMore.value) {
        return;
    }

    isFetchingMore.value = true;
    try {
        const response = await searchBankAccountsV2(
            buildV2SearchPayload({
                offset: nextOffset.value,
                limit: pageLimit,
                includeComments: true,
            }),
        );

        if (!response) {
            lastPageCount.value = 0;
            return;
        }

        const newItems = response.items ?? [];
        if (newItems.length > 0) {
            bankAccounts.value = [...bankAccounts.value, ...newItems];
            nextOffset.value += newItems.length;
            // ensure property names for newly loaded items
            void ensurePropertyNamesForRows(newItems);
        }
        lastPageCount.value = newItems.length;
        if (response.total !== undefined) {
            total.value = response.total;
        }
    } finally {
        isFetchingMore.value = false;
    }
};

const handleScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 200) {
        void loadMoreBankAccounts();
    }
};

// Helper to replace items within the current bankAccounts array at a given offset
function replaceItemsAtOffset(offset: number, newItems: BankAccount[]) {
    if (offset < 0) offset = 0;
    if (bankAccounts.value.length < offset) {
        bankAccounts.value = [...bankAccounts.value];
    }
    const before = bankAccounts.value.slice(0, offset);
    const after = bankAccounts.value.slice(offset + newItems.length);
    bankAccounts.value = [...before, ...newItems, ...after];
}

// Refetch current and previous page and merge results
async function refetchPages() {
    const currentOffset = Math.max(nextOffset.value - lastPageCount.value, 0);
    const prevOffset = Math.max(currentOffset - pageLimit, 0);

    const [currentResp, prevResp] = await Promise.all([
        searchBankAccountsV2(
            buildV2SearchPayload({
                offset: currentOffset,
                limit: pageLimit,
                includeComments: true,
            }),
        ),
        currentOffset > 0
            ? searchBankAccountsV2(
                  buildV2SearchPayload({
                      offset: prevOffset,
                      limit: pageLimit,
                      includeComments: true,
                  }),
              )
            : Promise.resolve(undefined),
    ]);

    if (currentResp?.items) {
        replaceItemsAtOffset(currentOffset, currentResp.items);
        lastPageCount.value = currentResp.items.length;
        total.value = currentResp.total;
        nextOffset.value = Math.max(nextOffset.value, currentOffset + currentResp.items.length);
    }

    if (prevResp?.items && currentOffset > 0) {
        replaceItemsAtOffset(prevOffset, prevResp.items);
        total.value = prevResp.total;
    }

    if (openedBankAccount.value) {
        const refreshed = bankAccounts.value.find((b) => b.id === openedBankAccount.value!.id);
        if (refreshed) {
            openedBankAccount.value = refreshed;
        }
    }
}

function handleDropdownSelect(key: string | number, row: BankAccount) {
    if (key === "view-notes") {
        const entity = bankAccounts.value.find((b) => b.id === row.id) || null;
        if (!entity) {
            message.error("Bank account not found");
            return;
        }
        openedBankAccount.value = entity;
        openedBankAccountId.value = entity.id; // store id in URL
        showDrawer.value = true;
        return;
    }

    if (key === "toggle-corporate") {
        const nextVal = !(row.is_corporate === true);
        requestToggleCorporate(row, nextVal);
        return;
    }
}

function handleCommentCreated(comment: Comment) {
    if (openedBankAccount.value) {
        const existing = (openedBankAccount.value as any).comments || [];
        openedBankAccount.value = {
            ...openedBankAccount.value,
            comments: [{ ...comment }, ...existing],
        } as BankAccount;
    }
    const idx = bankAccounts.value.findIndex((b) => b.id === openedBankAccount.value?.id);
    if (idx >= 0) {
        const target = bankAccounts.value[idx] as any;
        const existing = target.comments || [];
        bankAccounts.value[idx] = {
            ...target,
            comments: [{ ...comment }, ...existing],
        } as BankAccount;
    }
}

function handleCommentUpdated(updated: Comment) {
    if (openedBankAccount.value) {
        const list = (openedBankAccount.value as any).comments || [];
        const idx = list.findIndex((c: Comment) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            openedBankAccount.value = {
                ...openedBankAccount.value,
                comments: newList,
            } as BankAccount;
        }
    }
    const baIdx = bankAccounts.value.findIndex((b) => b.id === openedBankAccount.value?.id);
    if (baIdx >= 0) {
        const target: any = bankAccounts.value[baIdx];
        const list: Comment[] = target.comments || [];
        const idx = list.findIndex((c) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            bankAccounts.value[baIdx] = { ...target, comments: newList } as BankAccount;
        }
    }
}

function handleCommentDeleted(commentId: string) {
    if (openedBankAccount.value) {
        const filtered = ((openedBankAccount.value as any).comments || []).filter(
            (c: Comment) => c.id !== commentId,
        );
        openedBankAccount.value = {
            ...openedBankAccount.value,
            comments: filtered,
        } as BankAccount;
    }
    const idx = bankAccounts.value.findIndex((b) => b.id === openedBankAccount.value?.id);
    if (idx >= 0) {
        const target: any = bankAccounts.value[idx];
        const filtered = (target.comments || []).filter((c: Comment) => c.id !== commentId);
        bankAccounts.value[idx] = {
            ...target,
            comments: filtered,
        } as BankAccount;
    }
}

function openNotesForRow(row: BankAccount) {
    const entity = bankAccounts.value.find((b) => b.id === row.id) || row;
    openedBankAccount.value = entity;
    openedBankAccountId.value = entity.id;
    openedDrawerTab.value = "notes";
    showDrawer.value = true;
}

// Proper Scope patch flow
function useBankAccountPatch() {
    const updatingProperScope = ref<Record<string, boolean>>({});

    function setUpdating(id: string, val: boolean) {
        updatingProperScope.value = { ...updatingProperScope.value, [id]: val };
    }

    function isUpdating(id: string): boolean {
        return Boolean(updatingProperScope.value[id]);
    }

    function mergeBankAccountLocally(id: string, patch: Partial<BankAccount>) {
        const idx = bankAccounts.value.findIndex((b) => b.id === id);
        if (idx >= 0) {
            bankAccounts.value[idx] = { ...bankAccounts.value[idx], ...patch } as BankAccount;
        }
        if (openedBankAccount.value?.id === id) {
            openedBankAccount.value = { ...openedBankAccount.value, ...patch } as BankAccount;
        }
    }

    async function toggleProperScope(row: BankAccount, value: boolean) {
        if (isUpdating(row.id)) return;
        const original = row.proper_scope ?? null;

        setUpdating(row.id, true);
        try {
            const updated = await patchBankAccount(row.id, { proper_scope: value });
            if (updated) {
                mergeBankAccountLocally(row.id, updated as Partial<BankAccount>);
            } else {
                mergeBankAccountLocally(row.id, { proper_scope: value });
            }

            await refetchPages();

            if (value) {
                message.success("In Proper Scope");
            } else {
                message.warning("Out of Scope");
            }
        } catch (e) {
            mergeBankAccountLocally(row.id, { proper_scope: original ?? null } as any);
            message.error("Failed to update Proper Scope");
        } finally {
            setUpdating(row.id, false);
        }
    }

    const showProperScopeConfirm = ref(false);
    const pendingProperScopeRow = ref<BankAccount | null>(null);
    const pendingProperScopeValue = ref<boolean | null>(null);

    function requestToggleProperScope(row: BankAccount, value: boolean) {
        if (isUpdating(row.id)) return;
        pendingProperScopeRow.value = row;
        pendingProperScopeValue.value = value;
        showProperScopeConfirm.value = true;
    }

    function confirmToggleProperScope() {
        const row = pendingProperScopeRow.value;
        const val = pendingProperScopeValue.value;
        if (!row || val === null) {
            showProperScopeConfirm.value = false;
            pendingProperScopeRow.value = null;
            pendingProperScopeValue.value = null;
            return;
        }
        toggleProperScope(row, val as boolean);
        showProperScopeConfirm.value = false;
        pendingProperScopeRow.value = null;
        pendingProperScopeValue.value = null;
    }

    function cancelToggleProperScope() {
        showProperScopeConfirm.value = false;
        pendingProperScopeRow.value = null;
        pendingProperScopeValue.value = null;
    }

    return {
        isUpdating,
        showProperScopeConfirm,
        pendingProperScopeValue,
        requestToggleProperScope,
        confirmToggleProperScope,
        cancelToggleProperScope,
    };
}

const {
    isUpdating,
    showProperScopeConfirm,
    pendingProperScopeValue,
    requestToggleProperScope,
    confirmToggleProperScope,
    cancelToggleProperScope,
} = useBankAccountPatch();

// Corporate flag patch flow
function useBankAccountCorporatePatch() {
    const updatingCorporate = ref<Record<string, boolean>>({});

    function setUpdatingCorporate(id: string, val: boolean) {
        updatingCorporate.value = { ...updatingCorporate.value, [id]: val };
    }

    function isCorporateUpdating(id: string): boolean {
        return Boolean(updatingCorporate.value[id]);
    }

    function mergeBankAccountLocally(id: string, patch: Partial<BankAccount>) {
        const idx = bankAccounts.value.findIndex((b) => b.id === id);
        if (idx >= 0) {
            bankAccounts.value[idx] = { ...bankAccounts.value[idx], ...patch } as BankAccount;
        }
        if (openedBankAccount.value?.id === id) {
            openedBankAccount.value = { ...openedBankAccount.value, ...patch } as BankAccount;
        }
    }

    async function toggleCorporate(row: BankAccount, value: boolean) {
        if (isCorporateUpdating(row.id)) return;
        const original = row.is_corporate;

        setUpdatingCorporate(row.id, true);
        try {
            const updated = await patchBankAccount(row.id, { is_corporate: value });
            if (updated) {
                mergeBankAccountLocally(row.id, updated as Partial<BankAccount>);
            } else {
                mergeBankAccountLocally(row.id, { is_corporate: value });
            }

            await refetchPages();

            if (value) {
                message.success("Marked as Corporate");
            } else {
                message.success("Unmarked as Corporate");
            }
        } catch (e) {
            mergeBankAccountLocally(row.id, { is_corporate: original } as any);
            message.error("Failed to update Corporate flag");
        } finally {
            setUpdatingCorporate(row.id, false);
        }
    }

    const showCorporateConfirm = ref(false);
    const pendingCorporateRow = ref<BankAccount | null>(null);
    const pendingCorporateValue = ref<boolean | null>(null);

    function requestToggleCorporate(row: BankAccount, value: boolean) {
        if (isCorporateUpdating(row.id)) return;
        pendingCorporateRow.value = row;
        pendingCorporateValue.value = value;
        showCorporateConfirm.value = true;
    }

    function confirmToggleCorporate() {
        const row = pendingCorporateRow.value;
        const val = pendingCorporateValue.value;
        if (!row || val === null) {
            showCorporateConfirm.value = false;
            pendingCorporateRow.value = null;
            pendingCorporateValue.value = null;
            return;
        }
        toggleCorporate(row, val as boolean);
        showCorporateConfirm.value = false;
        pendingCorporateRow.value = null;
        pendingCorporateValue.value = null;
    }

    function cancelToggleCorporate() {
        showCorporateConfirm.value = false;
        pendingCorporateRow.value = null;
        pendingCorporateValue.value = null;
    }

    return {
        isCorporateUpdating,
        showCorporateConfirm,
        pendingCorporateValue,
        requestToggleCorporate,
        confirmToggleCorporate,
        cancelToggleCorporate,
    };
}

const {
    isCorporateUpdating,
    showCorporateConfirm,
    pendingCorporateValue,
    requestToggleCorporate,
    confirmToggleCorporate,
    cancelToggleCorporate,
} = useBankAccountCorporatePatch();

const columns = computed<DataTableColumns<BankAccount>>(() => [
    ...(props.showBulkScope ? [{ type: "selection" as const }] : []),
    {
        title: "ACCOUNT NUMBER",
        key: "account_number",
        width: isMobile.value ? 150 : "10%",
        minWidth: isMobile.value ? 150 : 150,
        render: (row) => h("span", { class: "font-semibold" }, row.account_number || "-"),
    },
    {
        title: "NAME",
        key: "account_name",
        width: isMobile.value ? 200 : "20%",
        minWidth: isMobile.value ? 200 : 200,
        render: (row) => row.account_name || "-",
    },
    {
        title: "PMS NAME",
        key: "pms",
        width: isMobile.value ? 100 : "5%",
        minWidth: isMobile.value ? 100 : 100,
        render: (row) => h("span", { class: "capitalize" }, row.pms || "-"),
    },
    {
        title: "BANK NAME",
        key: "bank_name",
        width: isMobile.value ? 250 : "20%",
        minWidth: isMobile.value ? 250 : 250,
        render: (row) => row.bank_name || "-",
    },
    {
        title: "PROPERTIES",
        key: "propertyBankAccounts",
        width: isMobile.value ? 250 : "20%",
        minWidth: isMobile.value ? 250 : 250,
        render: (row) => {
            const ids = (row.propertyBankAccounts || [])
                .map((pba) => pba?.propertyId)
                .filter((id): id is string => !!id);
            if (ids.length === 0) return "-";
            // if any ID is currently loading, render a skeleton
            const anyLoading = ids.some(
                (id) => inFlightPropertyIds.value.has(id) && !(id in propertyNameMap.value),
            );
            if (anyLoading) {
                return h("div", { class: "py-1" }, [
                    h(NSkeleton, { text: true, style: "width: 60%; height: 14px;" }),
                ]);
            }
            // render list-style property names, falling back to ID if name not found
            const items = ids.map((id) => propertyNameMap.value[id] || id);
            return h(
                "ul",
                { class: "list-disc pl-5 space-y-1" },
                items.map((label) => h("li", { class: "text-sm" }, label)),
            );
        },
    },
    {
        title: "PROPER SCOPE ",
        key: "proper_scope",
        width: 125,
        align: "center",
        render: (row) =>
            h(ProperSwitch as any, {
                modelValue: !!row.proper_scope,
                disabled: isUpdating(row.id),
                loading: isUpdating(row.id),
                "onUpdate:modelValue": (val: boolean) => requestToggleProperScope(row, val),
            }),
    },
    {
        title: "CORPORATE FLAG",
        key: "is_corporate",
        width: 145,
        align: "center",
        render: (row) =>
            row.is_corporate === true
                ? "Corporate"
                : row.is_corporate === false
                  ? "Non-corporate"
                  : "-",
    },
    {
        title: "NOTES",
        key: "comments",
        width: isMobile.value ? 200 : "35%",
        minWidth: isMobile.value ? 200 : 300,
        render: (row) =>
            renderNotesCell({
                row,
                onOpenNotes: openNotesForRow,
            }),
    },
    {
        title: "",
        key: "actions",
        width: 60,
        render: (row) =>
            h(
                NDropdown,
                {
                    options: [
                        {
                            label: row.is_corporate ? "Unmark as Corporate" : "Mark as Corporate",
                            key: "toggle-corporate",
                            disabled: isCorporateUpdating(row.id),
                        },
                        { label: "View Notes", key: "view-notes" },
                    ],
                    trigger: "click",
                    onSelect: (key: string | number) => handleDropdownSelect(key, row),
                },
                {
                    default: () =>
                        h(
                            "button",
                            {
                                class: "flex items-center justify-center w-full h-full cursor-pointer",
                            },
                            [h("span", { class: "material-symbols-outlined" }, "more_vert")],
                        ),
                },
            ),
    },
]);

const loading = computed(() => loadingBankAccounts.value);
</script>

<template>
    <div class="overflow-x-auto min-w-0">
        <div class="flex items-center justify-between mb-3">
            <!-- Search bar -->
            <proper-text
                v-model="searchTerm"
                placeholder="Search by Account Number, Name, PMS or Bank Name"
                class="flex-1 max-w-xl"
                :live-preview="false"
                :show-prefix="true"
                :clearable="true"
            />

            <!-- Filters -->
            <div class="flex items-center gap-2">
                <template v-if="hasActiveFilters">
                    <span class="text-xs text-neutral-600">{{ activeFiltersCount }} active</span>
                    <button class="text-xs underline active:scale-95" @click="clearFilters">
                        Clear
                    </button>
                </template>
                <n-popover style="width: 300px" trigger="click" v-model:show="filtersPopoverOpen">
                    <template #trigger>
                        <button class="secondary-button">Filters</button>
                    </template>
                    <div>
                        <div class="mb-3">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Proper Scope</label
                            >
                            <proper-select
                                v-model="filterProperScope"
                                :options="properScopeOptions"
                                placeholder="Proper Scope"
                                :clearable="true"
                            />
                        </div>
                        <div class="mb-3">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Corporate Flag</label
                            >
                            <proper-select
                                v-model="filterCorporateFlag"
                                :options="corporateFlagOptions"
                                placeholder="Corporate Flag"
                                :clearable="true"
                            />
                        </div>
                        <div class="mb-3">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Bank Name</label
                            >
                            <proper-select
                                v-model="filterBankNames"
                                :options="bankNameOptions"
                                placeholder="Bank Name"
                                multiple
                                :clearable="true"
                                :disabled="bankNamesLoading && !bankNamesFirstLoaded"
                                :loading="!bankNamesFirstLoaded && bankNamesLoading"
                            />
                        </div>
                        <div class="mb-1">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Properties</label
                            >
                            <proper-select
                                v-model="filterPropertyIds"
                                :options="propertyOptions"
                                placeholder="Properties"
                                multiple
                                :clearable="true"
                                :disabled="propertiesLoading && !propertiesFirstLoaded"
                                :loading="!propertiesFirstLoaded && propertiesLoading"
                            />
                        </div>
                        <div class="flex justify-end mt-2">
                            <button
                                v-if="hasActiveFilters"
                                class="text-xs underline active:scale-95"
                                @click="clearFilters"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </n-popover>
            </div>
        </div>
        <div
            v-if="showBulkScope && selectedRowKeys.length > 0"
            class="flex items-center gap-3 mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg"
        >
            <span class="text-sm font-medium">{{ selectedRowKeys.length }} selected</span>
            <button
                class="text-xs underline active:scale-95"
                @click="selectedRowKeys = bankAccounts.map((b) => b.id)"
            >
                Select All
            </button>
            <button class="text-xs underline active:scale-95" @click="selectedRowKeys = []">
                Deselect All
            </button>
            <button
                class="text-xs underline active:scale-95 ml-auto"
                :disabled="isBulkUpdating"
                @click="handleBulkScope(false)"
            >
                Set Out of Scope
            </button>
            <button
                class="button"
                :disabled="isBulkUpdating"
                @click="handleBulkScope(true)"
            >
                Set In Scope
            </button>
        </div>
        <n-data-table
            :columns="columns"
            :data="bankAccounts"
            :loading="loading || isFetchingMore"
            :bordered="true"
            :single-line="false"
            :single-column="false"
            :scroll-x="isMobile ? 970 : undefined"
            size="small"
            :class="['rounded-lg']"
            :max-height="isMobile ? '60vh' : '80vh'"
            :on-scroll="handleScroll"
            :row-key="(row: BankAccount) => row.id"
            :checked-row-keys="selectedRowKeys"
            @update:checked-row-keys="(keys: Array<string | number>) => (selectedRowKeys = keys.map(String))"
        >
            <template #loading>
                <div class="flex items-center justify-center mt-16 gap-2 bg-white p-3 rounded-lg">
                    <n-spin size="small" />
                    <p class="text-sm text-gray-500">Loading bank accounts</p>
                </div>
            </template>
            <template #empty>
                <div class="py-6 text-sm text-gray-400">No bank accounts found</div>
            </template>
        </n-data-table>
    </div>
    <notes-drawer
        v-model:show="showDrawer"
        v-model:tab="openedDrawerTab"
        :entity="openedBankAccount"
        type="bank_account"
        :auto-focus-on-open="true"
        @comment-created="handleCommentCreated"
        @comment-updated="handleCommentUpdated"
        @comment-deleted="handleCommentDeleted"
        @refetch="refetchPages"
    />

    <confirmation-modal
        v-if="showProperScopeConfirm"
        :danger="pendingProperScopeValue === false"
        :title="
            pendingProperScopeValue === false
                ? 'Confirm: set Out of Scope'
                : 'Confirm: set In Proper Scope'
        "
        :content="
            pendingProperScopeValue === false
                ? 'Are you sure you want to set this bank account Out of Scope?'
                : 'Are you sure you want to set this bank account In Proper Scope?'
        "
        negative-text="Cancel"
        :positive-text="pendingProperScopeValue === false ? 'Set Out of Scope' : 'Set In Scope'"
        @positive-click="confirmToggleProperScope"
        @negative-click="cancelToggleProperScope"
    />

    <confirmation-modal
        v-if="showCorporateConfirm"
        :title="
            pendingCorporateValue === true
                ? 'Confirm: Mark as Corporate'
                : 'Confirm: Unmark as Corporate'
        "
        :content="
            pendingCorporateValue === true
                ? 'Are you sure you want to mark this bank account as Corporate?'
                : 'Are you sure you want to unmark this bank account as Corporate?'
        "
        negative-text="Cancel"
        :positive-text="
            pendingCorporateValue === true ? 'Mark as Corporate' : 'Unmark as Corporate'
        "
        @positive-click="confirmToggleCorporate"
        @negative-click="cancelToggleCorporate"
    />
</template>
